import { Icon, log, useClickOutside } from "@hoylu/client-common";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { version } from "../../../package.json";
import { activateDashboardOption } from "../../state/workspaces/workspaces.actions";
import type { LocalizingFunction } from "../../utils/hooks/use.i18n";
import MenuItem from "../MoreOptions/MenuItem";
import { hasNewAnnouncements } from "../modals/AnnouncementsDialog";
import styles from "./help.menu.module.css";

type HelpMenuItem = {
  id: string;
  icon: string;
  /** If dashboardOption is set, it will dispatch the corresponding dashboard option instead of opening the i18n URL for that item */
  dashboardOption?: DashboardOption;
};

const menuItems: HelpMenuItem[] = [
  {
    id: "ANNOUNCEMENTS",
    icon: "hoylu-ui-icons-star-filled",
    dashboardOption: DashboardOption.ANNOUNCEMENTS,
  },
  { id: "GET_STARTED", icon: "hoylu-ui-icons-check" },
  { id: "TUTORIALS", icon: "hoylu-ui-icons-player-play" },
  { id: "KNOWLEDGE_BASE", icon: "hoylu-ui-icons-question-mark-filled" },
  { id: "PRODUCT_FEEDBACK", icon: "hoylu-ui-icons-bug" },
];

type HelpMenuProps = {
  localization: LocalizingFunction;
};

export const HelpMenu = ({ localization }: HelpMenuProps) => {
  const dispatch = useDispatch();
  const [componentVisible, setComponentVisible] = React.useState(false);

  const ref = useClickOutside(() => {
    setComponentVisible(false);
  });

  const handleMenuItemClick = (item: HelpMenuItem) => {
    if (item.dashboardOption) {
      dispatch(activateDashboardOption({ optionType: item.dashboardOption }));
    } else {
      window.open(localization(`${item.id}.URL`), "_blank");
    }
    setComponentVisible(false);
  };

  // Check for new announcements on mount and show Announcements modal if there are new ones
  // Note: This could also be triggered from somewhere else in the app, but since the help menu also has the button to open it, it makes sense to do it here
  useEffect(() => {
    const checkForNewAnnouncements = async () => {
      try {
        const hasNew = await hasNewAnnouncements();
        if (hasNew) {
          dispatch(
            activateDashboardOption({
              optionType: DashboardOption.ANNOUNCEMENTS,
            }),
          );
        }
      } catch (error) {
        log.error("Error checking for new announcements", error);
      }
    };
    checkForNewAnnouncements();
  }, [dispatch]);

  return (
    <div data-test-id="help-menu" ref={ref}>
      <div
        className={styles.helpMenuButton}
        data-test-id="help-menu-button"
        onClick={() =>
          setComponentVisible((componentVisible) => !componentVisible)
        }
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setComponentVisible((componentVisible) => !componentVisible);
          }
        }}
        role="button"
        tabIndex={0}
        title={localization("TOOLTIP")}
      >
        <Icon icon="hoylu-ui-icons-question-mark" />
      </div>
      {componentVisible && (
        <div className={styles.helpMenuContainer}>
          <div className={styles.helpMenu}>
            {menuItems.map((item) => (
              <MenuItem
                icon={item.icon}
                key={item.id}
                label={localization(`${item.id}.TITLE`)}
                onClick={() => handleMenuItemClick(item)}
                testId={`help-menu-${item.id}`}
                title={localization(`${item.id}.TOOLTIP`)}
              />
            ))}
            {/** biome-ignore lint/style/noJsxLiterals: Expected not localized */}
            <div className={styles.versionInfo}>Version {version}</div>
          </div>
        </div>
      )}
    </div>
  );
};
